import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import axios from "axios";
import io from "socket.io-client";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import TrikotFormDialog from "../components/TrikotFormDialog";
import UserManagement from "../components/UserManagement";
import { API_BASE_URL, SOCKET_URL } from "../config";
import SortingFilter from "../components/SortingFilter";

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trikots, setTrikots] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTrikot, setSelectedTrikot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortOption, setSortOption] = useState("number");
  const [sortDirection, setSortDirection] = useState("asc");
  const [tabValue, setTabValue] = useState(3);
  const [finishedTrikots, setFinishedTrikots] = useState([]);
  const [loadingFinished, setLoadingFinished] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [loadingStatistics, setLoadingStatistics] = useState(false);

  const fetchStatistics = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoadingStatistics(true);
      }
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/api/users/statistics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatistics(response.data.statistics);
      setError(null);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(
        "Fehler beim Laden der Statistiken: " +
          (err.response?.data?.message || "Verbindungsfehler zum Server")
      );
    } finally {
      if (showLoading) {
        setLoadingStatistics(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchTrikots();
  }, []);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    if (!user.isAdmin) {
      navigate("/");
      return;
    }

    fetchTrikots();
    // Load statistics if on statistics tab
    if (tabValue === 3) {
      fetchStatistics(true); // Show loading on initial load
    }
  }, [user, navigate, tabValue, fetchStatistics]);

  // Set up periodic statistics refresh when on statistics tab
  useEffect(() => {
    let intervalId;
    if (tabValue === 3) {
      // Refresh statistics every 5 seconds when on statistics tab
      intervalId = setInterval(() => {
        fetchStatistics(false); // No loading state for periodic updates
      }, 5000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [tabValue, fetchStatistics]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const socket = io(SOCKET_URL, {
      auth: {
        token: token || null,
      },
    });

    socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
      setError("Verbindungsfehler zum Server");
    });

    fetchTrikots();

    socket.on("trikotUpdate", ({ trikotId, updatedTrikot }) => {
      setTrikots((prevTrikots) =>
        prevTrikots.map((trikot) =>
          trikot._id === trikotId ? { ...trikot, ...updatedTrikot } : trikot
        )
      );
    });

    socket.on("newTrikot", (newTrikot) => {
      setTrikots((prevTrikots) => [...prevTrikots, newTrikot]);
    });

    socket.on("deleteTrikot", (deletedTrikotId) => {
      setTrikots((prevTrikots) =>
        prevTrikots.filter((trikot) => trikot._id !== deletedTrikotId)
      );
    });

    socket.on("bidUpdate", ({ trikotId, currentBid, highestBidder }) => {
      setTrikots((prevTrikots) => {
        const newTrikots = prevTrikots.map((trikot) =>
          trikot._id === trikotId
            ? { ...trikot, currentBid, highestBidder }
            : trikot
        );

        if (sortOption === "currentBid") {
          return [...newTrikots].sort((a, b) => b.currentBid - a.currentBid);
        }
        return newTrikots;
      });
    });

    // Listen for statistics updates
    socket.on("statisticsUpdate", () => {
      // Only update if we're on the statistics tab
      if (tabValue === 3) {
        fetchStatistics(false); // No loading state for socket updates
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [sortOption, tabValue, fetchStatistics]);

  const fetchTrikots = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/trikots`);
      console.log("Geladene Trikots mit Geboten:", response.data.trikots.map(t => ({
        number: t.number,
        playerName: t.playerName,
        currentBid: t.currentBid,
        highestBidder: t.highestBidder,
        startingBid: t.startingBid
      })));
      setTrikots(response.data.trikots);
      setError(null);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(
        "Fehler beim Laden der Trikots: " +
          (err.response?.data?.message || "Verbindungsfehler zum Server")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setSelectedTrikot(null);
    setOpenDialog(true);
  };

  const handleEdit = (trikot) => {
    setSelectedTrikot(trikot);
    setOpenDialog(true);
  };

  const handleDeleteBid = async (trikotId) => {
    if (!window.confirm("Möchten Sie dieses Trikot wirklich löschen?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/api/trikots/${trikotId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setTrikots((prevTrikots) =>
        prevTrikots.filter((trikot) => trikot._id !== trikotId)
      );
      setSuccess("Trikot erfolgreich gelöscht");
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(
        "Fehler beim Löschen des Trikots: " +
          (error.response?.data?.message || error.message)
      );
      setTimeout(() => setError(null), 5000);
    }
  };


  const getSortedTrikots = () => {
    const sorted = [...trikots].sort((a, b) => {
      switch (sortOption) {
        case "number":
          return parseInt(a.number) - parseInt(b.number);
        case "currentBid":
          return b.currentBid - a.currentBid;
        case "endTime":
          return new Date(a.endTime) - new Date(b.endTime);
        default:
          return 0;
      }
    });
    return sortDirection === "desc" ? sorted.reverse() : sorted;
  };

  const handleSortChange = (newValue) => {
    setSortOption(newValue);
  };

  const handleDirectionChange = (newDirection) => {
    setSortDirection(newDirection);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError(null);
    setSuccess(null);
    if (newValue === 0) {
      fetchTrikots();
    } else if (newValue === 2) {
      fetchFinishedTrikots();
    } else if (newValue === 3) {
      fetchStatistics(true); // Show loading when switching to statistics tab
    }
  };

  const fetchFinishedTrikots = async () => {
    try {
      setLoadingFinished(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/api/trikots/finished`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFinishedTrikots(response.data.trikots || []);
      setError(null);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(
        "Fehler beim Laden der beendeten Auktionen: " +
          (err.response?.data?.message || "Verbindungsfehler zum Server")
      );
    } finally {
      setLoadingFinished(false);
    }
  };

  const exportToCSV = () => {
    if (finishedTrikots.length === 0) {
      setError("Keine Daten zum Exportieren vorhanden");
      return;
    }

    // CSV Header
    const headers = [
      "Spielernummer",
      "Spielername",
      "Höchstgebot",
      "Vorname",
      "Nachname",
      "Benutzername",
      "Email",
    ];

    // CSV Daten
    const csvRows = [
      headers.join(","),
      ...finishedTrikots.map((trikot) => {
        const bidder = trikot.highestBidder || {};
        return [
          trikot.number || "",
          `"${(trikot.playerName || "").replace(/"/g, '""')}"`,
          trikot.currentBid || 0,
          `"${(bidder.firstName || "").replace(/"/g, '""')}"`,
          `"${(bidder.lastName || "").replace(/"/g, '""')}"`,
          `"${(bidder.username || "").replace(/"/g, '""')}"`,
          `"${(bidder.email || "").replace(/"/g, '""')}"`,
        ].join(",");
      }),
    ];

    const csvContent = csvRows.join("\n");

    // BOM für Excel-Kompatibilität
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", `beendete-auktionen-${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSave = async (trikotData) => {
    try {
      const token = localStorage.getItem("token");
      let response;

      if (selectedTrikot) {
        response = await axios.put(
          `${API_BASE_URL}/api/trikots/${selectedTrikot._id}`,
          trikotData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setTrikots((prevTrikots) =>
          prevTrikots.map((trikot) =>
            trikot._id === selectedTrikot._id ? response.data.trikot : trikot
          )
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/api/trikots`, trikotData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTrikots((prevTrikots) => [...prevTrikots, response.data.trikot]);
      }

      await fetchTrikots();
      setSuccess(
        selectedTrikot ? "Trikot aktualisiert" : "Neues Trikot erstellt"
      );
      setOpenDialog(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(
        "Fehler beim " +
          (selectedTrikot ? "Aktualisieren" : "Erstellen") +
          " des Trikots: " +
          (err.response?.data?.message || "Verbindungsfehler zum Server")
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString("de-DE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Berechne die Gesamtsumme der aktuellen Gebote
  const totalCurrentBids = trikots.reduce((sum, trikot) => sum + trikot.currentBid, 0);

  if (loading) {
    return (
      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
     
      <Box sx={{ width: "100%", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            "& .MuiTabs-scrollButtons": {
              "&.Mui-disabled": { opacity: 0.3 },
            },
          }}
        >
          <Tab label="Trikotverwaltung" />
          <Tab label="Benutzerverwaltung" />
          <Tab label="Beendete Auktionen" />
          <Tab label="Aktuelle Statistik" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <>
          <Box
            sx={{
              mb: 4,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Typography variant="h4" component="h2">
              Trikotverwaltung
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              fullWidth={false}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Neues Trikot
            </Button>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity="success"
              sx={{ mb: 3 }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}

          <SortingFilter
            sortOption={sortOption}
            onSortChange={handleSortChange}
            sortDirection={sortDirection}
            onDirectionChange={handleDirectionChange}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nummer</TableCell>
                  <TableCell>Spieler</TableCell>
                  <TableCell>Startgebot</TableCell>
                  <TableCell>Aktuelles Gebot</TableCell>
                  <TableCell>Höchstbietender</TableCell>
                  <TableCell>Enddatum</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trikots && trikots.length > 0 ? (
                  getSortedTrikots().map((trikot) =>
                    trikot._id ? (
                      <TableRow key={trikot._id}>
                        <TableCell>{trikot.number}</TableCell>
                        <TableCell>{trikot.playerName}</TableCell>
                        <TableCell>{trikot.startingBid}€</TableCell>
                        <TableCell>{trikot.currentBid}€</TableCell>
                        <TableCell>{trikot.highestBidder || '-'}</TableCell>
                        <TableCell>{formatDateTime(trikot.endTime)}</TableCell>
                        <TableCell>
                          {new Date(trikot.endTime) > new Date() ? (
                            <Typography color="primary">Aktiv</Typography>
                          ) : (
                            <Typography color="error">Beendet</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => handleEdit(trikot)}
                            color="primary"
                            title="Bearbeiten"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteBid(trikot._id)}
                            color="error"
                            title="Löschen"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ) : null
                  )
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Keine Trikots vorhanden
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell colSpan={3} align="left"><strong>Gesamtsumme:</strong></TableCell>
                  <TableCell align="left"><strong>{totalCurrentBids.toFixed(2)}€</strong></TableCell>
                  <TableCell colSpan={4}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <TrikotFormDialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            onSave={handleSave}
            trikot={selectedTrikot}
          />
        </>
      )}

      {tabValue === 1 && (
        <UserManagement onError={setError} onSuccess={setSuccess} />
      )}

      {tabValue === 2 && (
        <>
          <Box
            sx={{
              mb: 4,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h4" component="h2">
              Beendete Auktionen
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<FileDownloadIcon />}
              onClick={exportToCSV}
              disabled={finishedTrikots.length === 0 || loadingFinished}
            >
              Als CSV exportieren
            </Button>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {loadingFinished ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 3,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Spielernummer</TableCell>
                    <TableCell>Spielername</TableCell>
                    <TableCell>Höchstgebot</TableCell>
                    <TableCell>Vorname</TableCell>
                    <TableCell>Nachname</TableCell>
                    <TableCell>Benutzername</TableCell>
                    <TableCell>Email</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {finishedTrikots && finishedTrikots.length > 0 ? (
                    finishedTrikots.map((trikot) => {
                      const bidder = trikot.highestBidder || {};
                      return (
                        <TableRow key={trikot._id}>
                          <TableCell>{trikot.number || "-"}</TableCell>
                          <TableCell>{trikot.playerName || "-"}</TableCell>
                          <TableCell>
                            {trikot.currentBid
                              ? `${trikot.currentBid.toFixed(2)}€`
                              : "-"}
                          </TableCell>
                          <TableCell>{bidder.firstName || "-"}</TableCell>
                          <TableCell>{bidder.lastName || "-"}</TableCell>
                          <TableCell>{bidder.username || "-"}</TableCell>
                          <TableCell>{bidder.email || "-"}</TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Keine beendeten Auktionen vorhanden
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {tabValue === 3 && (
        <>
          <Box
            sx={{
              mb: 4,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h4" component="h2">
              Aktuelle Statistik
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => fetchStatistics(true)}
              disabled={loadingStatistics}
            >
              Aktualisieren
            </Button>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {loadingStatistics ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 3,
              }}
            >
              <CircularProgress />
            </Box>
          ) : statistics ? (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
                  gap: 3,
                }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    textAlign: "center",
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                  }}
                >
                  <Typography variant="h3" component="div" sx={{ mb: 1 }}>
                    {statistics.activeConnections}
                  </Typography>
                  <Typography variant="h6" component="div">
                    Aktive Verbindungen
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                    Benutzer aktuell online
                  </Typography>
                </Paper>

                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    textAlign: "center",
                    backgroundColor: "secondary.main",
                    color: "text.primary",
                  }}
                >
                  <Typography variant="h3" component="div" sx={{ mb: 1 }}>
                    {statistics.totalBids}
                  </Typography>
                  <Typography variant="h6" component="div">
                    Gesamte Gebote
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                    Bisher abgegeben
                  </Typography>
                </Paper>

                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    textAlign: "center",
                    backgroundColor: "success.main",
                    color: "success.contrastText",
                  }}
                >
                  <Typography variant="h3" component="div" sx={{ mb: 1 }}>
                    {statistics.registeredUsers}
                  </Typography>
                  <Typography variant="h6" component="div">
                    Registrierte Benutzer
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                    Gesamt im System
                  </Typography>
                </Paper>

                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    textAlign: "center",
                    backgroundColor: "info.main",
                    color: "info.contrastText",
                  }}
                >
                  <Typography variant="h3" component="div" sx={{ mb: 1 }}>
                    {statistics.maxConnections || statistics.activeConnections}
                  </Typography>
                  <Typography variant="h6" component="div">
                    Max. Verbindungen
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                    Höchstwert gleichzeitig
                  </Typography>
                </Paper>
              </Box>

              {statistics.totalSum !== undefined && (
                <Box sx={{ mt: 3 }}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 3,
                      textAlign: "center",
                      backgroundColor: "warning.main",
                      color: "warning.contrastText",
                    }}
                  >
                    <Typography variant="h3" component="div" sx={{ mb: 1 }}>
                      {new Intl.NumberFormat("de-DE", {
                        style: "currency",
                        currency: "EUR",
                      }).format(statistics.totalSum)}
                    </Typography>
                    <Typography variant="h6" component="div">
                      Gesamtsumme
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                      Summe aller aktuellen Gebote
                    </Typography>
                  </Paper>
                </Box>
              )}
            </>
          ) : (
            <Alert severity="info">
              Klicken Sie auf "Aktualisieren", um die Statistiken zu laden.
            </Alert>
          )}

          {statistics && statistics.connectedUsers && statistics.connectedUsers.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h6" component="div" sx={{ mb: 2 }}>
                  Verbundene Benutzer
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  {statistics.connectedUsers.map((username, index) => (
                    <Chip
                      key={index}
                      label={username}
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Box>
                {statistics.anonymousCount > 0 && (
                  <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                    {statistics.anonymousCount} anonyme Verbindung{statistics.anonymousCount !== 1 ? "en" : ""}
                  </Typography>
                )}
              </Paper>
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default AdminDashboard;